// src/pages/returnToVendor/CreateReturnToVendorPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw, X, Search, AlertCircle, Package } from 'lucide-react';
import { createReturnToVendor } from '../../services/api/procurement/returnToVendor';
import { getGoodsReceivedNote, getGoodsReceivedNotes } from '../../services/api/procurement/grn';
import { getUserWarehouses } from '../../services/api/purchaseOrder';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import { InfoCard } from '../../components/common';
import ProductThumb from '../procurement/components/ProductThumb';
import QuantityStepper from '../procurement/components/QuantityStepper';
import ps from '../procurement/components/ProcurementDetails.module.css';
import { PURCHASE_ORDER_BASE_PATH, getReturnTarget } from '../../utils/procurementNavigation';
import styles from './CreateReturnToVendorPage.module.css';

const CreateReturnToVendorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [grnIdInput, setGrnIdInput] = useState(searchParams.get('goodsReceivedNoteId') || '');
  const [grn, setGrn] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');

  // Populated only when the page is entered via ?purchaseOrderId= and that PO has more than
  // one GRN — the user then picks which one to return against before the items table shows.
  const [grnChoices, setGrnChoices] = useState([]);
  const [choicePurchaseOrder, setChoicePurchaseOrder] = useState(null);
  const [isResolvingPo, setIsResolvingPo] = useState(false);
  const [grnOptions, setGrnOptions] = useState([]);
  const [isSearchingGrn, setIsSearchingGrn] = useState(false);

  const [warehouses, setWarehouses] = useState([]);
  const [reason, setReason] = useState('');
  const [itemLines, setItemLines] = useState({}); // grnItemId -> { quantityReturned, unitPrice, returnFromWarehouseId }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const defaultBackTarget = grn?.purchaseOrder?.id ? `${PURCHASE_ORDER_BASE_PATH}/${grn.purchaseOrder.id}` : '/procurement/return-to-vendor';
  const defaultBackLabel = grn?.purchaseOrder?.purchaseOrderNumber || t('returnToVendor.backToReturnToVendors');
  const backTarget = getReturnTarget(location, searchParams, defaultBackTarget, defaultBackLabel);

  useEffect(() => {
    (async () => {
      try {
        const result = await getUserWarehouses();
        setWarehouses(result.warehouses || []);
      } catch (err) {
        console.error('Failed to load warehouses:', err);
      }
    })();
  }, []);

  useEffect(() => {
    const grnIdParam = searchParams.get('goodsReceivedNoteId');
    const poIdParam = searchParams.get('purchaseOrderId');
    if (grnIdParam) {
      handleLookupGrn(grnIdParam).catch((err) => {
        setGrn(null);
        setLookupError(err.response?.data?.message || err.message || t('returnToVendor.goodsReceivedNoteNotFound'));
      });
    } else if (poIdParam) {
      resolveGrnsForPurchaseOrder(poIdParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyGrn = (loadedGrn) => {
    setGrn(loadedGrn);
    setGrnOptions(prev => (prev.some(option => option.value === loadedGrn.id) ? prev : [toGrnOption(loadedGrn), ...prev]));
    const lines = {};
    (loadedGrn.items || []).forEach(item => {
      const returnableQuantity = Math.floor(Number(item.returnableQuantity ?? item.quantityReceived ?? 0));
      lines[item.id] = {
        quantityReturned: returnableQuantity,
        unitPrice: item.purchaseOrderItem?.unitPrice != null ? String(item.purchaseOrderItem.unitPrice) : '',
        returnFromWarehouseId: loadedGrn.warehouseId ? String(loadedGrn.warehouseId) : '',
      };
    });
    setItemLines(lines);
  };

  const resolveGrnsForPurchaseOrder = async (purchaseOrderId) => {
    try {
      setIsResolvingPo(true);
      setLookupError('');
      const { goodsReceivedNotes } = await getGoodsReceivedNotes({ purchaseOrderId });
      const list = goodsReceivedNotes || [];
      setChoicePurchaseOrder(prev => prev?.id === purchaseOrderId || String(prev?.id) === String(purchaseOrderId) ? prev : { id: purchaseOrderId });
      if (list.length === 0) {
        setLookupError(t('returnToVendor.goodsReceivedNoteNotFound'));
      } else if (list.length === 1) {
        setGrnChoices(list);
        await handleLookupGrn(list[0].id);
      } else {
        setGrnChoices(list);
      }
    } catch (err) {
      setLookupError(err.response?.data?.message || err.message || t('returnToVendor.goodsReceivedNoteNotFound'));
    } finally {
      setIsResolvingPo(false);
    }
  };

  const handleLookupGrn = async (idOverride) => {
    const value = String(idOverride ?? grnIdInput).trim();
    if (!value) {
      setLookupError(t('returnToVendor.goodsReceivedNoteNotFound'));
      return;
    }
    try {
      setIsLookingUp(true);
      setLookupError('');
      const { goodsReceivedNote } = await getGoodsReceivedNote(value);
      if (goodsReceivedNote.status === 'cancelled') {
        throw new Error(t('returnToVendor.goodsReceivedNoteCancelled', 'Cannot return against a cancelled goods received note'));
      }
      setGrnIdInput(goodsReceivedNote.grnNumber || String(goodsReceivedNote.id));
      applyGrn(goodsReceivedNote);
    } catch (err) {
      throw err;
    } finally {
      setIsLookingUp(false);
    }
  };

  const toGrnOption = (g) => ({
    value: g.id,
    label: `${g.grnNumber}${g.purchaseOrder?.purchaseOrderNumber ? ` — ${g.purchaseOrder.purchaseOrderNumber}` : ''}${g.receivedAt ? ` — ${new Date(g.receivedAt).toLocaleDateString()}` : ''}`,
  });

  // Searchable lookup: GRNs matching the typed GRN number or purchase order number
  // (backend search covers both); selecting one runs the existing handleLookupGrn.
  const searchGoodsReceivedNotes = async (term = '') => {
    try {
      setIsSearchingGrn(true);
      const { goodsReceivedNotes } = await getGoodsReceivedNotes({ search: term.trim() || undefined, limit: 20 });
      setGrnOptions((goodsReceivedNotes || []).filter(g => g.status !== 'cancelled').map(toGrnOption));
    } catch (err) {
      console.error('Failed to search goods received notes:', err);
      setGrnOptions([]);
    } finally {
      setIsSearchingGrn(false);
    }
  };

  useEffect(() => {
    searchGoodsReceivedNotes('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectGrnOption = (value) => {
    if (!value) return;
    setLookupError('');
    handleLookupGrn(value).catch((err) => {
      setGrn(null);
      setLookupError(err.response?.data?.message || err.message || t('returnToVendor.goodsReceivedNoteNotFound'));
    });
  };

  const updateLine = (grnItemId, patch) => {
    setItemLines(prev => ({ ...prev, [grnItemId]: { ...prev[grnItemId], ...patch } }));
  };

  const updateQuantity = (grnItemId, value, max) => {
    const clamped = Math.min(Math.max(0, Math.floor(parseFloat(value) || 0)), Math.floor(max));
    updateLine(grnItemId, { quantityReturned: clamped });
  };

  const hasItemsSelected = Object.values(itemLines).some(l => Number(l.quantityReturned) > 0);

  const validateForm = () => {
    const newErrors = {};
    if (!grn) newErrors.grn = t('returnToVendor.goodsReceivedNoteRequired');
    if (!hasItemsSelected) newErrors.items = t('returnToVendor.atLeastOneItemRequired');
    Object.entries(itemLines).forEach(([grnItemId, line]) => {
      if (Number(line.quantityReturned) > 0) {
        if (!line.unitPrice || Number(line.unitPrice) <= 0) newErrors[`unitPrice_${grnItemId}`] = t('returnToVendor.unitPriceRequired');
        if (!line.returnFromWarehouseId) newErrors[`warehouse_${grnItemId}`] = t('returnToVendor.warehouseRequired');
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      error(t('returnToVendor.someFieldsRequired'), { title: t('common.validationError') });
      return;
    }

    setIsSubmitting(true);
    try {
      const items = Object.entries(itemLines)
        .filter(([, line]) => Number(line.quantityReturned) > 0)
        .map(([grnItemId, line]) => ({
          grnItemId: parseInt(grnItemId, 10),
          quantityReturned: Number(line.quantityReturned),
          unitPrice: Number(line.unitPrice),
          returnFromWarehouseId: parseInt(line.returnFromWarehouseId, 10),
        }));

      const payload = {
        purchaseOrderId: grn.purchaseOrder.id,
        goodsReceivedNoteId: grn.id,
        items,
        reason: reason.trim() || undefined,
      };

      const response = await createReturnToVendor(payload);
      success(
        t('returnToVendor.rtvCreatedSuccessWithDebitNote', {
          rtvNumber: response.rtv?.rtvNumber,
          debitNoteNumber: response.debitNote?.debitNoteNumber,
        }),
        { title: t('common.success') }
      );
      navigate(`/procurement/return-to-vendor/${response.rtv.id}`);
    } catch (err) {
      console.error('Return to vendor creation error:', err);
      error(err.response?.data?.message || t('returnToVendor.failedToCreateReturnToVendor'), {
        title: t('common.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSelected = Object.values(itemLines).reduce((sum, l) => sum + (Number(l.quantityReturned) || 0), 0);

  return (
    <div className={styles.createReturnToVendorPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <RotateCcw className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('returnToVendor.createReturnToVendor')}</h1>
              <p>{t('returnToVendor.createReturnToVendorDescription')}</p>
            </div>
          </div>
          <button onClick={() => navigate(backTarget.path)} className={styles.backButton}>
            <ArrowLeft size={16} />
            {backTarget.label || t('returnToVendor.backToReturnToVendors')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={styles.rtvForm}>
        <InfoCard icon={Search} title={t('returnToVendor.lookupGoodsReceivedNote')}>
          <div className={styles.lookupRowWrapper}>
            <div className={styles.lookupRow}>
              <Dropdown
                className={styles.lookupDropdown}
                value={grn?.id || ''}
                onChange={handleSelectGrnOption}
                options={grnOptions}
                placeholder={isLookingUp ? t('common.loading') : t('returnToVendor.goodsReceivedNoteIdPlaceholder')}
                searchPlaceholder={t('returnToVendor.searchGoodsReceivedNote')}
                onSearch={searchGoodsReceivedNotes}
                loading={isSearchingGrn}
                loadingText={t('common.loading')}
                noOptionsText={t('returnToVendor.noGoodsReceivedNotesFound')}
                allowClear={false}
                disabled={isLookingUp}
              />
            </div>
            {grn && (
              <div className={styles.orderSummary}>
                <span><strong>{grn.grnNumber}</strong></span>
                <span>{grn.purchaseOrder?.purchaseOrderNumber ? `#${grn.purchaseOrder.purchaseOrderNumber}` : ''}</span>
              </div>
            )}
          </div>

          {grnChoices.length > 0 && (
            <div className={styles.grnChoiceWrapper}>
              <label>{t('returnToVendor.selectGoodsReceivedNote')}</label>
              <Dropdown
                value={grn?.id || ''}
                onChange={(value) => value && handleLookupGrn(value).catch((err) => {
                  setLookupError(err.response?.data?.message || err.message || t('returnToVendor.goodsReceivedNoteNotFound'));
                })}
                options={grnChoices.map(g => ({
                  value: g.id,
                  label: `${g.grnNumber}${g.receivedAt ? ` — ${new Date(g.receivedAt).toLocaleDateString()}` : ''}`,
                }))}
                placeholder={choicePurchaseOrder?.purchaseOrderNumber || t('returnToVendor.multipleGoodsReceivedNotesFound')}
                searchable
              />
            </div>
          )}

          {isResolvingPo && <div className={styles.sectionError}>{t('common.loading')}</div>}
          {lookupError && <div className={styles.sectionError}><AlertCircle size={16} />{lookupError}</div>}
          {errors.grn && <div className={styles.sectionError}><AlertCircle size={16} />{errors.grn}</div>}
        </InfoCard>

        {grn && (
          <div className={styles.topColumns}>
            <div className={styles.leftColumn}>
              <InfoCard icon={RotateCcw} title={t('returnToVendor.details')}>
                <div className={styles.fieldGroup}>
                  <label>{t('returnToVendor.reason')}</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={4}
                    className={styles.textarea}
                    placeholder={t('returnToVendor.reasonPlaceholder')}
                  />
                </div>
              </InfoCard>
            </div>

            <div className={styles.rightColumn}>
              <InfoCard icon={Package} title={t('returnToVendor.itemsToReturn')}>
                <div className={ps.tableWrapper}>
                <table className={ps.itemsTable}>
                  <thead>
                    <tr>
                      <th>{t('returnToVendor.item')}</th>
                      <th className={ps.alignRight}>{t('returnToVendor.returnableQuantity', 'Returnable Qty')}</th>
                      <th className={ps.alignRight}>{t('returnToVendor.quantityReturned')}</th>
                      <th className={ps.alignRight}>{t('returnToVendor.unitPriceLabel')}</th>
                      <th>{t('returnToVendor.returnFromWarehouse')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(grn.items || []).map(item => {
                      const line = itemLines[item.id] || {};
                      const max = Math.floor(Number(item.returnableQuantity ?? item.quantityReceived ?? 0));
                      return (
                        <tr key={item.id}>
                          <td>
                            <div className={ps.productCellInner}>
                              <ProductThumb item={item.purchaseOrderItem} alt={item.purchaseOrderItem?.Product?.name || ''} />
                              <div className={ps.productCell}>
                                <span className={ps.productName}>{item.purchaseOrderItem?.Product?.name}</span>
                                {(item.purchaseOrderItem?.ProductVariation?.sku || item.purchaseOrderItem?.Product?.sku) && <span className={ps.productSku}>SKU: {(item.purchaseOrderItem?.ProductVariation?.sku || item.purchaseOrderItem?.Product?.sku)}</span>}
                              </div>
                            </div>
                          </td>
                          <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>
                            {max}
                          </td>
                          <td className={ps.alignRight}>
                            <QuantityStepper
                              value={line.quantityReturned ?? 0}
                              onChange={(value) => updateQuantity(item.id, value, max)}
                              min={0}
                              max={max}
                            />
                          </td>
                          <td className={ps.alignRight}>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.unitPrice ?? ''}
                              onChange={(e) => updateLine(item.id, { unitPrice: e.target.value })}
                              className={styles.qtyInput}
                            />
                            {errors[`unitPrice_${item.id}`] && (
                              <div className={styles.fieldError}>{errors[`unitPrice_${item.id}`]}</div>
                            )}
                          </td>
                          <td>
                            <Dropdown
                              value={line.returnFromWarehouseId || ''}
                              onChange={(value) => updateLine(item.id, { returnFromWarehouseId: value || '' })}
                              options={warehouses.map(w => ({ value: w.id, label: w.name }))}
                              placeholder={t('returnToVendor.selectWarehouse')}
                              allowClear={false}
                              disabled
                              size="compact"
                            />
                            {errors[`warehouse_${item.id}`] && (
                              <div className={styles.fieldError}>{errors[`warehouse_${item.id}`]}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                {errors.items && <div className={styles.sectionError}><AlertCircle size={16} />{errors.items}</div>}
              </InfoCard>
            </div>
          </div>
        )}

        <div className={styles.actionBarSpacer} />
      </form>

      <div className={styles.fixedActionBar}>
        <div className={styles.actionBarInner}>
          <div className={styles.actionBarMeta}>
            {totalSelected > 0 && (
              <span className={styles.actionBarItemCount}>
                {totalSelected} {t('returnToVendor.unitsSelected')}
              </span>
            )}
          </div>
          <div className={styles.actionBarButtons}>
            <button type="button" onClick={() => navigate(backTarget.path)} className={styles.cancelButton} disabled={isSubmitting}>
              <X size={15} />
              {t('common.cancel')}
            </button>
            <button type="button" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting || !grn}>
              {isSubmitting ? <div className={styles.loadingSpinner} /> : (
                <>
                  <Save size={15} />
                  {t('returnToVendor.createReturnToVendor')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateReturnToVendorPage;
